import { useApolloClient, useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import FilestackButton, {
    PickerResponse,
} from '@deltek/specification-client/core/Widgets/Filestack/FilestackButton'
import { CircularProgress, IconButton, Tooltip, Typography } from '@material-ui/core'
import { Theme, makeStyles } from '@material-ui/core/styles'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import PublishIcon from '@material-ui/icons/Publish'
import { ListingDetail } from '@deltek/specification-client/core/Models/listing'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import {
    ProductListingDetailInput,
    ProductListingDetailPayload,
} from '@deltek/specification-client/core/Models/productListingDetail'
import { SaveProductListingDetail } from 'bpm/api/graphQL/mutations/productListing.mutation'
import { ProductListingDetailsQuery } from 'bpm/api/graphQL/queries/product.queries'
import { processEnv } from 'components/configuration/environmentVariables'
import React, { useCallback, useEffect, useReducer } from 'react'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import { listingDetailToSaveInput } from './listingDetailUtils'
import {
    MAX_FILENAME_LENGTH,
    MAX_UPLOAD_SIZE_BYTES,
    SUCCESS_INDICATOR_DURATION_MS,
} from './importButtonCell.constants'

const useStyles = makeStyles((theme: Theme) => ({
    iconButton: {
        padding: theme.spacing(0.5),
    },
    successIcon: {
        color: '#27AE60',
        fontSize: pxToRem(20),
    },
    uploadIcon: {
        color: '#1976d2',
        fontSize: pxToRem(20),
    },
    cellContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        overflow: 'hidden',
        maxWidth: '100%',
    },
    catalogName: {
        fontSize: pxToRem(12),
        fontFamily: 'Roboto',
        flex: '1 1 0',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
}))

interface ImportCatalogButtonCellProps {
    row: { original: { listingId: string; type?: ListingLevel; catalogs?: Array<{ title?: string }> } }
}

// Per-listing upload state — module level to survive virtualized remounts
const uploadingCache: Record<string, boolean> = {}
const successCache: Record<string, boolean> = {}
const uploadedNameCache: Record<string, string> = {}

/**
 * Admin-only upload button for product catalog PDFs. Accepts one .pdf file up to 2 GB.
 * Shows the existing catalog filename alongside a replace/upload button.
 * Used for column: importCatalog.
 */
const ImportCatalogButtonCell = ({ row }: ImportCatalogButtonCellProps) => {
    const classes = useStyles()
    const listingId: string | undefined = row?.original?.listingId
    const isManufacturerListing = row?.original?.type === ListingLevel.Manufacturer
    const existingCatalogName =
        uploadedNameCache[listingId] ??
        (row?.original?.catalogs ?? [])[0]?.title ??
        null
    const [, forceUpdate] = useReducer((x) => x + 1, 0)
    const { showGlobalSnackbar } = useGlobalSnackbar()
    const client = useApolloClient()

    const [showGlobalSnackbarMutation] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const isUploading = !!uploadingCache[listingId]
    const isSuccess = !!successCache[listingId]

    // Clear success indicator after 3s, with cleanup on unmount
    useEffect(() => {
        if (!isSuccess) return
        const timerId = setTimeout(() => {
            delete successCache[listingId]
            forceUpdate()
        }, SUCCESS_INDICATOR_DURATION_MS)
        return () => clearTimeout(timerId)
    }, [isSuccess, listingId])

    // Clear uploading state on unmount to prevent a permanent spinner if the row remounts mid-upload.
    // successCache and uploadedNameCache are intentionally long-lived across remounts — they persist
    // the uploaded filename and success indicator so re-entering the page shows the correct state.
    useEffect(() => () => { delete uploadingCache[listingId] }, [listingId])

    const onFilepickerDone = useCallback(
        async (pickerResponse: PickerResponse) => {
            if (pickerResponse.filesFailed.length) {
                forceUpdate()
                showGlobalSnackbar({
                    variables: {
                        message: 'Error uploading catalog. Please try again.',
                        variant: 'error',
                        id: `ImportCatalog_UploadError_${listingId}`,
                    },
                })
                return
            }

            uploadingCache[listingId] = true
            forceUpdate()

            try {
                // Fetch full detail — SaveProductListingDetail requires complete payload
                const fetchResult = await client.query<{
                    getListingDetail: Partial<ListingDetail>
                }>({
                    query: ProductListingDetailsQuery,
                    variables: {
                        listingIdInput: {
                            listingId,
                            logEventSource: 'BpmListingDetail',
                        },
                    },
                    fetchPolicy: 'network-only',
                })

                const detail = fetchResult?.data?.getListingDetail
                if (!detail) throw new Error('Could not load listing detail')

                const uploadedFile = pickerResponse.filesUploaded[0]
                const newCatalog = {
                    title: uploadedFile.filename.substring(0, MAX_FILENAME_LENGTH),
                    fileUploadDocument: {
                        fileName: uploadedFile.key,
                        fileNameTarget: uploadedFile.filename,
                    },
                }

                await client.mutate<
                    { saveProductListingDetail: Partial<ProductListingDetailPayload> },
                    { saveProductListingDetailInput: Partial<ProductListingDetailInput> }
                >({
                    mutation: SaveProductListingDetail,
                    variables: {
                        saveProductListingDetailInput: listingDetailToSaveInput(
                            detail,
                            { catalogs: [newCatalog] }
                        ),
                    },
                })

                delete uploadingCache[listingId]
                successCache[listingId] = true
                uploadedNameCache[listingId] = uploadedFile.filename
                forceUpdate()
            } catch (error: unknown) {
                delete uploadingCache[listingId]
                forceUpdate()
                showGlobalSnackbar({
                    variables: {
                        message:
                            error instanceof Error
                                ? error.message
                                : 'Failed to save catalog. Please try again.',
                        variant: 'error',
                        id: `ImportCatalog_SaveError_${listingId}`,
                    },
                })
            }
        },
        [listingId, client, showGlobalSnackbar]
    )

    if (!listingId) return <></>

    if (isUploading) {
        return <CircularProgress size={16} />
    }

    if (isSuccess) {
        return (
            <Tooltip title="Catalog uploaded successfully">
                <CheckCircleIcon className={classes.successIcon} />
            </Tooltip>
        )
    }

    const uploadButton = (
        <FilestackButton
            apiKey={processEnv.REACT_APP_FileStack_ApiKey}
            kpCoreUri={processEnv.REACT_APP_KPCoreUri}
            pickerOptions={{
                accept: ['.pdf'],
                maxFiles: 1,
                maxSize: MAX_UPLOAD_SIZE_BYTES,
                onFileSelected: (file) => {
                    if (!file.size || file.size === 0) {
                        throw new Error(
                            'File has 0KB size. Please upload a file with content.'
                        )
                    }
                },
            }}
            showGlobalSnackbar={showGlobalSnackbarMutation}
            onFilepickerDone={onFilepickerDone}
            renderButton={(onClick) => (
                <Tooltip title={existingCatalogName ? 'Replace Catalog (.pdf)' : 'Upload Catalog (.pdf)'}>
                    <IconButton
                        className={classes.iconButton}
                        onClick={onClick}
                        size="small"
                        aria-label="Upload Catalog"
                    >
                        <PublishIcon className={classes.uploadIcon} />
                    </IconButton>
                </Tooltip>
            )}
        />
    )

    return (
        <div className={classes.cellContainer}>
            {existingCatalogName && (
                <Tooltip title={existingCatalogName}>
                    <Typography className={classes.catalogName}>{existingCatalogName}</Typography>
                </Tooltip>
            )}
            {!isManufacturerListing && uploadButton}
        </div>
    )
}

export default ImportCatalogButtonCell
